import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { REGIONS, type Meta, type ProfileResponse } from '@jetopolis/shared';
import { api } from '../../app/api/client';
import { useAuth } from '../../app/stores/auth';
import { useOverlays } from '../../app/stores/overlays';
import { Button } from '../../ui/Button';
import { Panel, PanelHeader } from '../../ui/Panel';
import { LocalePicker } from './LocalePicker';

export function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, refresh } = useAuth();
  const openAuth = useOverlays((s) => s.open);

  const [meta, setMeta] = useState<Meta | null>(null);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setMeta(await api.get<Meta>('/v1/meta'));
      } catch {
        // Версия сборки не критична для работы экрана.
      }
      if (!user) return;
      try {
        setProfile(await api.get<ProfileResponse>('/v1/profile'));
      } catch {
        // Профиля может не быть до первой правки.
      }
    })();
  }, [user]);

  const setRegion = async (region: string): Promise<void> => {
    await api.patch('/v1/profile', { region });
    setProfile(await api.get<ProfileResponse>('/v1/profile'));
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[640px] flex-col gap-4 px-4 py-4">
      <div className="flex items-center gap-3">
        <Link to="/lobby" className="text-[12px] text-mut hover:text-txt">
          {t('common.action.back')}
        </Link>
        <h1 className="font-[family-name:var(--font-display)] text-[20px] text-txt">
          {t('settings.title')}
        </h1>
      </div>

      <Panel>
        <PanelHeader>{t('settings.language')}</PanelHeader>
        <div className="p-4">
          <LocalePicker />
        </div>
      </Panel>

      <Panel>
        <PanelHeader>{t('settings.region')}</PanelHeader>
        <div className="p-4">
          <select
            value={profile?.region ?? ''}
            aria-label={t('settings.region')}
            onChange={(e) => {
              void setRegion(e.target.value);
            }}
            className="min-h-[var(--spacing-touch)] w-full rounded-[6px] border border-line2 bg-panel2 px-2 text-[13px] text-txt outline-none focus:border-amber"
          >
            <option value="">-</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {t(`lobby.region.${r}`)}
              </option>
            ))}
          </select>
        </div>
      </Panel>

      <Panel>
        <PanelHeader>{t('settings.account')}</PanelHeader>
        <div className="flex flex-col gap-3 p-4">
          {user === null && <p className="text-[12px] text-mut">{t('errors.auth.required')}</p>}
          {user?.isAnonymous === true && (
            <>
              <p className="text-[12px] text-mut">{t('auth.guest.linkExplain')}</p>
              <Button
                variant="primary"
                onClick={() => {
                  openAuth('auth');
                }}
              >
                {t('auth.guest.linkCta')}
              </Button>
            </>
          )}
          {user !== null && user.isAnonymous === false && (
            <p className="text-[12px] text-mut">
              {t('auth.signedInAs', { name: user.email ?? user.name ?? user.id })}
            </p>
          )}
          {user !== null && (
            <Button
              variant="danger"
              onClick={() => {
                void (async () => {
                  await signOut();
                  await refresh();
                  await navigate('/');
                })();
              }}
            >
              {t('common.action.signOut')}
            </Button>
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader>{t('settings.version')}</PanelHeader>
        <div className="tabular p-4 text-[11px] text-dim">
          {t('settings.protocol')}: v{meta?.apiVersion ?? '-'} / day v
          {meta?.dayPayloadVersion ?? '-'}
        </div>
      </Panel>
    </main>
  );
}
